import { Button, Icon, IconButton, Stack, Typography } from '@mui/material';
import { DATE_FORMAT_RANGE_FNS, DATE_FORMAT_WEEK_DAY_FNS } from '@schooly/api';
import { DateSelect } from '@schooly/components/filters';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  InformationBlock,
  Loading,
  ModalSmall,
  Skeleton,
  Spin,
  TimeIcon,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, ReactNode } from 'react';
import { FormProvider } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ModalContent,
  ModalFooter,
  ModalPanel,
} from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { RegisterGroupsStep } from './RegisterGroupsStep';
import { useRegisterCreateModal } from './useRegisterCreateModal';

export const RegisterCreateModal: React.FC = () => {
  const { formatMessage } = useIntl();
  const {
    form,
    step,
    isEdit,
    activeTab,
    switchTab,
    date,
    minDate,
    maxDate,
    compositionLoading,
    eligibleGroupsLoading,
    eligibleGroupsFetching,
    eligibleGroups,
    timetableStructures,
    periodsByStructureId,
    timetableStructuresLoading,
    availableTabs,
    addedGroups,
    groupIds,
    initialRosterIds,
    saving,
    deleting,
    checkingDate,
    errors,
    actions,
  } = useRegisterCreateModal();

  const isDateStep = !isEdit && step === 'date';
  const actionsDisabled = saving || deleting || compositionLoading;
  const registerDate = date ? newDateTimezoneOffset(date) : undefined;
  const showDateHeader = !isDateStep && !!registerDate;

  const renderContent = () => {
    if (compositionLoading) return <Loading />;

    if (isDateStep) {
      return (
        <ModalPanel active>
          <Stack gap={2}>
            <DateSelect
              date={date}
              onSetDate={actions.onDateChange}
              requiredLabel="required"
              placeholder={formatMessage({ id: 'attendance-RegisterDate' })}
              CalendarProps={{ minDate, maxDate }}
              error={
                errors.register_date
                  ? { type: 'validate', message: errors.register_date.message || '' }
                  : undefined
              }
            />
            <RegisterCreateRulesInfo />
          </Stack>
        </ModalPanel>
      );
    }

    return (
      <RegisterGroupsStep
        loading={eligibleGroupsLoading}
        fetching={eligibleGroupsFetching}
        eligibleGroups={eligibleGroups}
        activeTab={activeTab}
        onSwitchTab={switchTab}
        availableTabs={availableTabs}
        addedGroups={addedGroups}
        groupIds={groupIds}
        initialRosterIds={initialRosterIds}
        isEdit={isEdit}
        timetableStructures={timetableStructures}
        periodsByStructureId={periodsByStructureId}
        timetableStructuresLoading={timetableStructuresLoading}
        onAddGroupId={actions.addGroupId}
        onRemoveGroupId={actions.removeGroupId}
      />
    );
  };

  return (
    <ModalSmall open onClose={() => actions.close()}>
      <FormProvider {...form}>
        <form onSubmit={actions.submit}>
          <ModalHeader
            active
            multiline
            title={
              showDateHeader ? (
                <Stack>
                  <Typography variant="h2">
                    {compositionLoading ? (
                      <Skeleton variant="text" width={160} />
                    ) : (
                      format(registerDate, DATE_FORMAT_RANGE_FNS)
                    )}
                  </Typography>
                  <Typography variant="h4" color="common.grey2">
                    {compositionLoading ? (
                      <Skeleton variant="text" width={80} />
                    ) : (
                      format(registerDate, DATE_FORMAT_WEEK_DAY_FNS)
                    )}
                  </Typography>
                </Stack>
              ) : (
                formatMessage({ id: 'attendance-Register' })
              )
            }
            sx={{ py: showDateHeader ? 1.5 : undefined, alignItems: 'center' }}
          >
            <IconButton onClick={() => actions.close()}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>

          <ModalContent active sx={{ padding: 0 }}>
            {renderContent()}
          </ModalContent>

          <ModalFooter active>
            <Stack
              direction="row"
              width="100%"
              justifyContent={isDateStep ? 'flex-end' : 'space-between'}
            >
              {!isDateStep && !isEdit && (
                <Button
                  key="back"
                  type="button"
                  onClick={() => actions.goToDate()}
                  variant="outlined"
                  startIcon={<ArrowLeftIcon />}
                  disabled={actionsDisabled}
                >
                  <FormattedMessage id="action-Back" />
                </Button>
              )}
              {isEdit && (
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={deleting ? <Spin /> : <DeleteIcon />}
                  disabled={actionsDisabled}
                  onClick={() => actions.remove()}
                >
                  <FormattedMessage id="action-Delete" />
                </Button>
              )}
              {isDateStep ? (
                <Button
                  type="button"
                  endIcon={checkingDate ? <Spin /> : <ArrowRightIcon />}
                  disabled={!date || checkingDate}
                  onClick={actions.goToGroups}
                >
                  <FormattedMessage id="action-Continue" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={actionsDisabled}
                  endIcon={saving ? <Spin /> : <CheckIcon />}
                  sx={isEdit ? { ml: 'auto' } : undefined}
                >
                  <FormattedMessage id={isEdit ? 'action-Save' : 'action-Create'} />
                </Button>
              )}
            </Stack>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalSmall>
  );
};

export const RegisterCreateRulesInfo: FC = () => (
  <InformationBlock
    alignItems="stretch"
    direction="column"
    sx={{
      p: 2,
      bgcolor: 'common.lightBg',
    }}
  >
    <Typography variant="h2" pb={0.5}>
      <FormattedMessage id="attendance-RegisterAutoFillRulesTitle" />
    </Typography>
    <Stack gap={2}>
      <RegisterCreateRuleItem
        icon={<TimeIcon />}
        titleId="attendance-RegisterAutoFillRulesFrequenciesTitle"
        descriptionId="attendance-RegisterAutoFillRulesFrequenciesDescription"
      />
      <RegisterCreateRuleItem
        icon={<CalendarIcon />}
        titleId="attendance-RegisterAutoFillRulesTimetableTitle"
        descriptionId="attendance-RegisterAutoFillRulesTimetableDescription"
      />
    </Stack>
  </InformationBlock>
);

type RegisterCreateRuleItemProps = {
  icon: ReactNode;
  titleId: string;
  descriptionId: string;
};

const RegisterCreateRuleItem: FC<RegisterCreateRuleItemProps> = ({
  icon,
  titleId,
  descriptionId,
}) => (
  <Stack direction="row" gap={2.25} alignItems="flex-start">
    <Stack
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: 'common.white',
        color: 'primary.main',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon fontSize="medium">{icon}</Icon>
    </Stack>
    <Stack>
      <Typography variant="h3">
        <FormattedMessage id={titleId} />
      </Typography>
      <Typography>
        <FormattedMessage id={descriptionId} />
      </Typography>
    </Stack>
  </Stack>
);
